import "./MetaDocTableOfContents.scss";

import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import { KaboodleToaster } from "components/common";
import DatePicker from "components/inputs/DatePicker/DatePicker";
import Select from "components/inputs/Select/Select";
import TextInput from "components/inputs/TextInput/TextInput";
import { FEATURE_FLAGS } from "constants/FeatureFlags";
import { useMemo, useState } from "react";
import { setShowDocumentsDrawer } from "reducers/drawer";
import { useUpdateDocumentGroupMutation } from "services/document-group-api";
import { DOCUMENT_GROUP_TAG_TYPES } from "services/document-group-tag-api";
import { DATE_TO_STRING, STRING_TO_DATE } from "utils/date-utils";
import { useDispatch, useSelector } from "utils/redux";

import * as Sentry from "@sentry/react";

import type { GroupingSource } from "services/metadoc-api";
import {
  CLONE_REASONS,
  getCloneReasonLabel,
  METADOC_FOCUS_RESTORE_DELAY_MS,
  METADOC_GROUP_ID_PREFIX,
  type CloneReason,
} from "../../MetadocConstants";
import { useTocScrollSync } from "../../hooks/navigation/useTocScrollSync";
import { useMetadocOptions } from "../../hooks/page-operations/useMetadocOptions";
import { getDocumentTypeLabel } from "../../utils/documentTypeUtils";
import {
  createFacilityByDocumentGroupId,
  matchesTocEntryFilters,
} from "../../utils/tocFilterUtils";
import { useEnabledDocumentGroupTagTypes } from "../../hooks/page-operations/useEnabledDocumentGroupTagTypes";
import BulkTagByDateRangeModal from "./BulkTagByDateRangeModal";
import DeleteTocEntryModal from "./DeleteTocEntryModal";
import DocumentGroupTagMenu from "./DocumentGroupTagMenu";
import DocumentTypePalette from "./DocumentTypePalette";
import TocActionsMenu, { type TocAction } from "./TocActionsMenu";
import EditableMetadocName from "./EditableMetadocName";

import type { OptionType } from "components/inputs/Select/Select";
import type { MultiValue } from "react-select";
import type { DocumentGroup } from "services/document-group-api";
import type {
  DocumentGroupTag,
  DocumentGroupTagType,
} from "services/document-group-tag-api";
import type { TocEntry } from "../../metadoc.types";
import type { UntaggedPageDirection } from "../../utils/untaggedPageUtils";

export type { TocEntry };

type MetaDocTableOfContentsProps = {
  entries: TocEntry[];
  currentPage: number;
  onPageSelect: (page: number) => void;
  onCategorySelect?: (category: string) => void;
  documentGroups: DocumentGroup[];
  onDeleteEntry?: (entryId: string) => Promise<void>;
  metadocId?: number;
  metadocName?: string;
  disabled?: boolean;
  selectedPages?: Set<number>;
  totalPages?: number;
  onAssignDocumentType?: (type: string) => Promise<boolean>;
  onAssignDocumentTypeToRange?: (
    type: string,
    startPage: number,
    endPage: number,
  ) => Promise<boolean>;
  onAssignDocumentGroupTag?: (
    documentGroupId: number,
    type: DocumentGroupTagType,
  ) => Promise<boolean>;
  onRemoveDocumentGroupTag?: (
    documentGroupTagId: number,
    type: DocumentGroupTagType,
  ) => Promise<void>;
  onDocumentTypeChangeComplete?: () => void;
  onRegenerationJobCreated?: (jobId: number) => void;
  onRegenerationRequestStateChange?: (isRequesting: boolean) => void;
  onPendingDocumentTypeChange?: (groupId: number, documentType: string) => void;
  onPendingDateChange?: (groupId: number, date: string | null) => void;
  onPendingMetadataChange?: (
    groupId: number,
    field: "provider" | "facility",
    value: string | null,
  ) => void;
  onCancelPendingDelete?: (groupId: number) => void;
  facilityByDocumentGroupIdOverride?: Map<number, string>;
  hasPendingChanges?: boolean;
  pendingChangeCount?: number;
  onApplyPendingChanges?: () => void;
  onDiscardPendingChanges?: () => void;
  isApplyingPendingChanges?: boolean;
  pageClassificationStats?: {
    coveredPages: number;
    totalPages: number;
    percentage: number;
  };
  metadocStatus?: string;
  hasUntaggedPages?: boolean;
  onGoToUntaggedPage?: (direction: UntaggedPageDirection) => void;
  groupingSource?: GroupingSource;
  onGroupingSourceChange?: (source: GroupingSource) => void;
  canShowGroupingToggle?: boolean;
  isOpsPostAiReadOnly?: boolean;
  cloneReason?: CloneReason | null;
  selectedDocumentTypes?: string[];
  hasExplicitDocumentTypeSelection?: boolean;
  searchQuery?: string;
  onSearchQueryChange?: (value: string) => void;
  pageRangeStart?: number | null;
  pageRangeEnd?: number | null;
  dateRangeStart?: string | null;
  dateRangeEnd?: string | null;
  matchingDateDocumentGroupIds?: Set<number>;
  matchingMedicationDocumentGroupIds?: Set<number>;
  matchingCodeDocumentGroupIds?: Set<number>;
  medicationSearchQuery?: string;
  codeSearchQuery?: string;
};

const MetaDocTableOfContents = ({
  entries,
  currentPage,
  onPageSelect,
  onCategorySelect,
  documentGroups,
  onDeleteEntry,
  metadocId,
  metadocName,
  disabled = false,
  selectedPages,
  totalPages = 0,
  onAssignDocumentType,
  onAssignDocumentTypeToRange,
  onAssignDocumentGroupTag,
  onRemoveDocumentGroupTag,
  onDocumentTypeChangeComplete,
  onRegenerationJobCreated,
  onRegenerationRequestStateChange,
  onPendingDocumentTypeChange,
  onPendingDateChange,
  onPendingMetadataChange,
  onCancelPendingDelete,
  facilityByDocumentGroupIdOverride,
  hasPendingChanges = false,
  pendingChangeCount = 0,
  onApplyPendingChanges,
  onDiscardPendingChanges,
  isApplyingPendingChanges = false,
  pageClassificationStats,
  metadocStatus,
  hasUntaggedPages = false,
  onGoToUntaggedPage,
  groupingSource = "OPS",
  onGroupingSourceChange,
  canShowGroupingToggle = false,
  isOpsPostAiReadOnly = false,
  cloneReason = null,
  selectedDocumentTypes = [],
  hasExplicitDocumentTypeSelection = false,
  searchQuery,
  onSearchQueryChange,
  pageRangeStart = null,
  pageRangeEnd = null,
  dateRangeStart = null,
  dateRangeEnd = null,
  matchingDateDocumentGroupIds,
  matchingMedicationDocumentGroupIds,
  matchingCodeDocumentGroupIds,
  medicationSearchQuery = "",
  codeSearchQuery = "",
}: MetaDocTableOfContentsProps) => {
  // Lock all entry-level controls while a batch Apply is in flight, not just
  // the Apply/Discard buttons — prevents new edits on top of an in-flight batch.
  disabled = disabled || isApplyingPendingChanges;

  const dispatch = useDispatch();
  const { getDocumentTypeColor, options } = useMetadocOptions();
  const [updateDocumentGroup] = useUpdateDocumentGroupMutation();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null);
  const [entryToDelete, setEntryToDelete] = useState<TocEntry | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [updatingEntryId, setUpdatingEntryId] = useState<string | null>(null);
  const [tagActionEntryId, setTagActionEntryId] = useState<string | null>(null);
  const [isBulkTagModalOpen, setIsBulkTagModalOpen] = useState(false);
  const [editableMetadataByEntryId, setEditableMetadataByEntryId] = useState<
    Record<string, { facility: string; provider: string }>
  >({});
  const constants = useSelector((state) => state.shared.constants);
  const isPostAiEnabled =
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.METADOC_POST_AI_ENABLED];
  const isMetadocCloningEnabled =
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.METADOC_CLONING_ENABLED];
  const isDateRangeTaggingEnabled = Boolean(
    constants?.FEATURE_FLAGS?.[
      FEATURE_FLAGS.METADOC_CMS_SUBMISSION_DATE_RANGE_TAGGING_ENABLED
    ],
  );
  const enabledTagTypes = useEnabledDocumentGroupTagTypes();
  const isAiAnalysisReadOnly = isPostAiEnabled && groupingSource === "AI";
  const isSelectiveOpsReadonlyMode =
    isOpsPostAiReadOnly &&
    groupingSource === "OPS" &&
    cloneReason === CLONE_REASONS.AI_ANALYSIS;
  const isEntryReadOnly = (entry: TocEntry): boolean =>
    isSelectiveOpsReadonlyMode ? Boolean(entry.isCloned) : isOpsPostAiReadOnly;
  const facilityByDocumentGroupIdInternal = useMemo(
    () => createFacilityByDocumentGroupId(documentGroups),
    [documentGroups],
  );
  const facilityByDocumentGroupId =
    facilityByDocumentGroupIdOverride ?? facilityByDocumentGroupIdInternal;
  const tocSearchQuery = searchQuery ?? "";

  const filteredEntries = entries.filter((entry) => {
    const matchesCategory =
      !selectedCategory || entry.category === selectedCategory;
    const matchesFilter = matchesTocEntryFilters(
      entry,
      {
        selectedDocumentTypes,
        hasExplicitDocumentTypeSelection,
        searchQuery: tocSearchQuery,
        pageRangeStart,
        pageRangeEnd,
        dateRangeStart,
        dateRangeEnd,
        matchingDateDocumentGroupIds,
        matchingMedicationDocumentGroupIds,
        matchingCodeDocumentGroupIds,
        medicationSearchQuery,
        codeSearchQuery,
      },
      { facilityByDocumentGroupId },
    );

    return matchesCategory && matchesFilter;
  });

  const renderList = useMemo(
    () => [...filteredEntries].sort((a, b) => a.startPage - b.startPage),
    [filteredEntries],
  );

  const handleEntryClick = (entry: TocEntry) => {
    if (!disabled) {
      onPageSelect(entry.startPage);
    }
  };

  const handleCategoryFilter = (category: string | null) => {
    setSelectedCategory(category);
    if (category && onCategorySelect) {
      onCategorySelect(category);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, entry: TocEntry) => {
    e.stopPropagation();

    if (
      disabled ||
      isEntryReadOnly(entry) ||
      !onDeleteEntry ||
      deletingEntryId
    ) {
      return;
    }

    setEntryToDelete(entry);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!entryToDelete || !onDeleteEntry) return;

    setDeletingEntryId(entryToDelete.id);
    try {
      await onDeleteEntry(entryToDelete.id);
      setIsDeleteModalOpen(false);
      setEntryToDelete(null);
    } finally {
      setDeletingEntryId(null);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setEntryToDelete(null);
  };

  const handleDocumentTypeChange = async (
    entry: TocEntry,
    selectedOption:
      | OptionType
      | OptionType[]
      | MultiValue<OptionType | OptionType[]>
      | null,
  ) => {
    if (
      !selectedOption ||
      disabled ||
      isEntryReadOnly(entry) ||
      updatingEntryId
    ) {
      return;
    }

    // handle single select (not multi-select)
    const option = Array.isArray(selectedOption)
      ? selectedOption[0]
      : (selectedOption as OptionType);

    if (!option) {
      return;
    }

    const newDocumentType = option.value as string;
    if (newDocumentType === entry.category) {
      return; // no change needed
    }

    // use documentGroupId if available, otherwise extract from entry ID
    const groupId =
      entry.documentGroupId ??
      parseInt(entry.id.replace(METADOC_GROUP_ID_PREFIX, ""), 10);

    if (isNaN(groupId)) {
      // log to Sentry for monitoring
      Sentry.captureMessage("Invalid document group ID", {
        level: "error",
        tags: { feature: "metadoc", action: "document_type_change" },
        extra: { entryId: entry.id, groupId },
      });
      KaboodleToaster.error("Invalid document group ID");
      return;
    }

    if (onPendingDocumentTypeChange) {
      onPendingDocumentTypeChange(groupId, newDocumentType);
      return;
    }

    setUpdatingEntryId(entry.id);
    let hasRegenerationJob = false;
    onRegenerationRequestStateChange?.(true);

    try {
      const response = await updateDocumentGroup({
        id: groupId,
        document_type: newDocumentType,
      }).unwrap();

      if (response.background_job_id) {
        hasRegenerationJob = true;
        onRegenerationJobCreated?.(response.background_job_id);
      }

      const displayLabel = getDocumentTypeLabel(newDocumentType);
      KaboodleToaster.success(`Document type updated to "${displayLabel}"`);

      // return focus to thumbnails after successful category change
      if (onDocumentTypeChangeComplete) {
        setTimeout(() => {
          onDocumentTypeChangeComplete();
        }, METADOC_FOCUS_RESTORE_DELAY_MS);
      }
    } catch (error) {
      // log to Sentry for monitoring
      Sentry.captureException(error, {
        tags: { feature: "metadoc", action: "document_group_update" },
        extra: { groupId, newDocumentType },
      });
      KaboodleToaster.error(
        "Failed to update document type. Please try again.",
      );
    } finally {
      setUpdatingEntryId(null);
      if (!hasRegenerationJob) {
        onRegenerationRequestStateChange?.(false);
      }
    }
  };

  const getDocumentGroupId = (entry: TocEntry): number | null => {
    const resolvedGroupId =
      entry.documentGroupId ??
      parseInt(entry.id.replace(METADOC_GROUP_ID_PREFIX, ""), 10);
    return isNaN(resolvedGroupId) ? null : resolvedGroupId;
  };

  const handleAssignTag = async (
    entry: TocEntry,
    type: DocumentGroupTagType,
  ) => {
    const groupId = getDocumentGroupId(entry);
    if (
      !onAssignDocumentGroupTag ||
      groupId === null ||
      disabled ||
      tagActionEntryId
    ) {
      return;
    }

    setTagActionEntryId(entry.id);
    try {
      await onAssignDocumentGroupTag(groupId, type);
    } finally {
      setTagActionEntryId(null);
    }
  };

  const handleRemoveTag = async (entry: TocEntry, tag: DocumentGroupTag) => {
    if (!onRemoveDocumentGroupTag || disabled || tagActionEntryId) {
      return;
    }

    setTagActionEntryId(entry.id);
    try {
      await onRemoveDocumentGroupTag(tag.id, tag.type);
    } finally {
      setTagActionEntryId(null);
    }
  };

  const handleDocumentDateChange = async (
    entry: TocEntry,
    selectedDate: Date | null,
  ) => {
    if (disabled || updatingEntryId) {
      return;
    }

    const groupId = getDocumentGroupId(entry);
    if (!groupId) {
      Sentry.captureMessage("Invalid document group ID", {
        level: "error",
        tags: { feature: "metadoc", action: "document_date_change" },
        extra: { entryId: entry.id, groupId },
      });
      KaboodleToaster.error("Invalid document group ID");
      return;
    }

    const nextDate = DATE_TO_STRING(selectedDate, "yyyy-MM-dd");
    const currentDate = DATE_TO_STRING(
      STRING_TO_DATE(entry.dateOfService ?? null),
      "yyyy-MM-dd",
    );

    if (nextDate === currentDate) {
      return;
    }

    if (onPendingDateChange) {
      onPendingDateChange(groupId, nextDate);
      return;
    }

    setUpdatingEntryId(entry.id);
    let hasRegenerationJob = false;
    onRegenerationRequestStateChange?.(true);

    try {
      const response = await updateDocumentGroup({
        id: groupId,
        date: nextDate,
      }).unwrap();

      if (response.background_job_id) {
        hasRegenerationJob = true;
        onRegenerationJobCreated?.(response.background_job_id);
      }

      KaboodleToaster.success("Document date updated");
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: "metadoc", action: "document_group_date_update" },
        extra: { groupId, date: nextDate },
      });
      KaboodleToaster.error(
        "Failed to update document date. Please try again.",
      );
    } finally {
      setUpdatingEntryId(null);
      if (!hasRegenerationJob) {
        onRegenerationRequestStateChange?.(false);
      }
    }
  };

  const getEditableMetadataValue = (
    entry: TocEntry,
    field: "facility" | "provider",
    fallbackValue: string,
  ): string => {
    return editableMetadataByEntryId[entry.id]?.[field] ?? fallbackValue;
  };

  const handleEditableMetadataChange = (
    entry: TocEntry,
    field: "facility" | "provider",
    value: string,
  ) => {
    const groupId = getDocumentGroupId(entry);
    const currentFacility = groupId
      ? (facilityByDocumentGroupId.get(groupId) ?? "")
      : "";
    const currentProvider = entry.provider ?? "";

    setEditableMetadataByEntryId((currentValues) => ({
      ...currentValues,
      [entry.id]: {
        provider: currentValues[entry.id]?.provider ?? currentProvider,
        facility: currentValues[entry.id]?.facility ?? currentFacility,
        [field]: value,
      },
    }));
  };

  const handleDocumentMetadataSave = async (
    entry: TocEntry,
    field: "facility" | "provider",
    value: string,
  ) => {
    if (disabled || updatingEntryId) {
      return;
    }

    const groupId = getDocumentGroupId(entry);
    if (!groupId) {
      Sentry.captureMessage("Invalid document group ID", {
        level: "error",
        tags: { feature: "metadoc", action: `document_${field}_change` },
        extra: { entryId: entry.id, groupId },
      });
      KaboodleToaster.error("Invalid document group ID");
      return;
    }

    const normalizedValue = value.trim();
    const currentValue =
      field === "provider"
        ? (entry.provider ?? "").trim()
        : (facilityByDocumentGroupId.get(groupId) ?? "").trim();

    if (normalizedValue === currentValue) {
      return;
    }

    if (onPendingMetadataChange) {
      onPendingMetadataChange(groupId, field, normalizedValue || null);
      setEditableMetadataByEntryId((currentValues) => {
        const nextValues = { ...currentValues };
        delete nextValues[entry.id];
        return nextValues;
      });
      return;
    }

    setUpdatingEntryId(entry.id);

    try {
      await updateDocumentGroup({
        id: groupId,
        [field]: normalizedValue || null,
      }).unwrap();

      KaboodleToaster.success(
        `Document ${field === "provider" ? "provider" : "facility"} updated`,
      );

      setEditableMetadataByEntryId((currentValues) => {
        const nextValues = { ...currentValues };
        delete nextValues[entry.id];
        return nextValues;
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: "metadoc", action: `document_group_${field}_update` },
        extra: { groupId, [field]: normalizedValue || null },
      });
      KaboodleToaster.error(
        `Failed to update document ${field === "provider" ? "provider" : "facility"}. Please try again.`,
      );
    } finally {
      setUpdatingEntryId(null);
    }
  };

  // get active entry based on current page
  const getActiveEntry = () => {
    return filteredEntries.find((entry) => {
      const endPage = entry.endPage || entry.startPage;
      return currentPage >= entry.startPage && currentPage <= endPage;
    });
  };

  const activeEntry = getActiveEntry();

  // use scroll sync hook (pass renderList, the actual displayed order,
  // since Records for Rated Age groups are interleaved differently than filteredEntries' flat order)
  const { listContainerRef } = useTocScrollSync({
    currentPage,
    activeEntry,
    filteredEntries: renderList,
  });
  const isGroupingToggleDisabled = disabled || !onGroupingSourceChange;
  const cloneReasonLabel = getCloneReasonLabel(cloneReason);
  const showAddDocumentsButton =
    isMetadocCloningEnabled && cloneReason === CLONE_REASONS.AI_ANALYSIS;
  const handleGroupingSourceChange = (source: GroupingSource) => {
    if (isGroupingToggleDisabled || source === groupingSource) {
      return;
    }

    onSearchQueryChange?.("");
    setSelectedCategory(null);
    onGroupingSourceChange?.(source);
  };
  const canBulkTagByDateRange =
    isDateRangeTaggingEnabled &&
    groupingSource === "AI" &&
    metadocId !== undefined &&
    !isOpsPostAiReadOnly &&
    Boolean(onAssignDocumentGroupTag) &&
    enabledTagTypes.includes(DOCUMENT_GROUP_TAG_TYPES.CMS_SUBMISSION);
  const tocActions: TocAction[] = canBulkTagByDateRange
    ? [
        {
          label: "Tag Medical Records as CMS Submission",
          icon: "date_range",
          onSelect: () => setIsBulkTagModalOpen(true),
          disabledReason: hasPendingChanges
            ? "Apply or discard unsaved changes first"
            : null,
        },
      ]
    : [];
  const handleAddDocuments = () => {
    dispatch(setShowDocumentsDrawer(true));
  };

  return (
    <>
      <DeleteTocEntryModal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        entry={entryToDelete}
        isDeleting={!!deletingEntryId}
        groupingSource={groupingSource}
      />
      {isBulkTagModalOpen && metadocId !== undefined && (
        <BulkTagByDateRangeModal
          onClose={() => setIsBulkTagModalOpen(false)}
          metadocId={metadocId}
          groupingSource={groupingSource}
          entries={entries}
        />
      )}
      <div className="metadoc-toc">
        <div className="metadoc-toc__header">
          {metadocName && metadocId && (
            <EditableMetadocName
              metadocId={metadocId}
              metadocName={metadocName}
              className="metadoc-toc__metadoc-name"
            />
          )}
          {metadocStatus === "completed" && pageClassificationStats && (
            <div
              className={`metadoc-toc__classification-progress ${
                pageClassificationStats.percentage === 100
                  ? "metadoc-toc__classification-progress--complete"
                  : ""
              }`}
            >
              <div className="metadoc-toc__classification-progress__bar">
                <div
                  className="metadoc-toc__classification-progress__fill"
                  style={{ width: `${pageClassificationStats.percentage}%` }}
                />
              </div>
              <span className="metadoc-toc__classification-progress__label">
                {pageClassificationStats.coveredPages}/
                {pageClassificationStats.totalPages} pages grouped
              </span>
            </div>
          )}
          {cloneReasonLabel && (
            <div className="metadoc-toc__clone-reason">
              <div className="metadoc-toc__clone-reason-row">
                <span className="metadoc-toc__grouping-label">
                  Clone Reason
                </span>
                <div className="metadoc-grouping-toggle metadoc-grouping-toggle--readonly">
                  <span className="metadoc-grouping-toggle__value">
                    <span className="material-icons">content_copy</span>
                    {cloneReasonLabel}
                  </span>
                </div>
              </div>
              {showAddDocumentsButton && (
                <button
                  type="button"
                  className="metadoc-toc__add-documents-btn"
                  onClick={handleAddDocuments}
                  disabled={disabled}
                >
                  <span className="material-icons">add</span>
                  Add Documents
                </button>
              )}
            </div>
          )}
          {metadocStatus === "completed" &&
            pageClassificationStats &&
            canShowGroupingToggle &&
            onGroupingSourceChange &&
            isPostAiEnabled && (
              <div className="metadoc-toc__grouping-toggle">
                <span className="metadoc-toc__grouping-label">Grouping</span>
                <div className="metadoc-grouping-toggle" role="group">
                  <button
                    type="button"
                    className={`metadoc-grouping-toggle__btn${
                      groupingSource === "OPS"
                        ? " metadoc-grouping-toggle__btn--active"
                        : ""
                    }`}
                    onClick={() => handleGroupingSourceChange("OPS")}
                    disabled={isGroupingToggleDisabled}
                    aria-pressed={groupingSource === "OPS"}
                  >
                    <span className="material-icons">assignment</span>
                    Ops
                  </button>
                  <button
                    type="button"
                    className={`metadoc-grouping-toggle__btn${
                      groupingSource === "AI"
                        ? " metadoc-grouping-toggle__btn--active"
                        : ""
                    }`}
                    onClick={() => handleGroupingSourceChange("AI")}
                    disabled={isGroupingToggleDisabled}
                    aria-pressed={groupingSource === "AI"}
                  >
                    <span className="material-icons">auto_awesome</span>
                    Analyst
                  </button>
                </div>
              </div>
            )}
          {tocActions.length > 0 && (
            <div className="metadoc-toc__actions-row">
              <span className="metadoc-toc__grouping-label">Actions</span>
              <TocActionsMenu disabled={disabled} actions={tocActions} />
            </div>
          )}
          {metadocStatus === "completed" &&
            pageClassificationStats &&
            onGoToUntaggedPage && (
              <div className="metadoc-toc__untagged-nav">
                <span className="metadoc-toc__grouping-label">
                  Untagged pages
                </span>
                <div className="metadoc-toc__untagged-nav__actions">
                  <ButtonIcon
                    size="small"
                    title="Previous untagged page"
                    aria-label="Previous untagged page"
                    onClick={() => onGoToUntaggedPage("previous")}
                    disabled={disabled || !hasUntaggedPages}
                  >
                    arrow_back
                  </ButtonIcon>
                  <ButtonIcon
                    size="small"
                    title="Next untagged page"
                    aria-label="Next untagged page"
                    onClick={() => onGoToUntaggedPage("next")}
                    disabled={disabled || !hasUntaggedPages}
                  >
                    arrow_forward
                  </ButtonIcon>
                </div>
              </div>
            )}
          {hasPendingChanges && (
            <div className="metadoc-toc__pending-bar">
              <span className="metadoc-toc__pending-bar__count">
                <span className="material-icons">pending</span>
                {pendingChangeCount}{" "}
                {pendingChangeCount === 1
                  ? "unsaved change"
                  : "unsaved changes"}
              </span>
              <div className="metadoc-toc__pending-bar__actions">
                <button
                  type="button"
                  className="metadoc-toc__pending-bar__discard"
                  onClick={onDiscardPendingChanges}
                  disabled={disabled}
                  title="Discard all pending changes"
                >
                  Discard
                </button>
                <button
                  type="button"
                  className="metadoc-toc__pending-bar__apply"
                  onClick={onApplyPendingChanges}
                  disabled={disabled}
                  title="Apply all pending changes"
                >
                  {isApplyingPendingChanges ? (
                    <>
                      <span className="material-icons metadoc-toc__pending-bar__spinner">
                        sync
                      </span>
                      Applying…
                    </>
                  ) : (
                    "Apply"
                  )}
                </button>
              </div>
            </div>
          )}
          {selectedCategory && (
            <div className="metadoc-toc__filter-badge">
              <span>Filtered by: {selectedCategory}</span>
              <button
                onClick={() => handleCategoryFilter(null)}
                aria-label="Clear filter"
              >
                <span className="material-icons">close</span>
              </button>
            </div>
          )}
        </div>

        <div className="metadoc-toc__list" ref={listContainerRef}>
          {renderList.length === 0 ? (
            <div className="metadoc-toc__empty">
              <span className="material-icons">search_off</span>
              <p>No Table of Contents found</p>
            </div>
          ) : (
            renderList.map((entry) => {
              const isActive = activeEntry?.id === entry.id;
              const pageRange =
                entry.endPage && entry.endPage !== entry.startPage
                  ? `${entry.startPage}-${entry.endPage}`
                  : `${entry.startPage}`;

              const isDocumentGroup = entry.id.startsWith(
                METADOC_GROUP_ID_PREFIX,
              );

              const categoryColor = getDocumentTypeColor(entry.category);
              const displayLabel = getDocumentTypeLabel(entry.category);
              const facility = entry.documentGroupId
                ? facilityByDocumentGroupId.get(entry.documentGroupId)
                : undefined;
              const isAiEditableDocumentGroup =
                isDocumentGroup &&
                groupingSource === "AI" &&
                !isEntryReadOnly(entry);
              const providerValue = getEditableMetadataValue(
                entry,
                "provider",
                entry.provider ?? "",
              );
              const facilityValue = getEditableMetadataValue(
                entry,
                "facility",
                facility ?? "",
              );

              return (
                <div
                  key={entry.id}
                  data-entry-id={entry.id}
                  className={`metadoc-toc__entry ${isActive ? "metadoc-toc__entry--active" : ""} ${isDocumentGroup ? "metadoc-toc__entry--document-group" : ""} ${disabled ? "metadoc-toc__entry--disabled" : ""} ${entry.hasPendingChange ? "metadoc-toc__entry--pending" : ""} ${entry.isPendingDelete ? "metadoc-toc__entry--pending-delete" : ""}`}
                  onClick={() => handleEntryClick(entry)}
                  style={{
                    borderLeftColor: categoryColor,
                  }}
                  title={entry.category}
                >
                  <div className="metadoc-toc__entry-header">
                    <span
                      className="metadoc-toc__entry-page"
                      style={{ backgroundColor: `${categoryColor}15` }}
                    >
                      {pageRange}
                    </span>
                    <div className="metadoc-toc__entry-category">
                      {isDocumentGroup && (
                        <>
                          <span
                            className="material-icons metadoc-toc__entry-icon"
                            style={{ color: categoryColor }}
                            title="Document Group"
                          >
                            label
                          </span>
                          {isEntryReadOnly(entry) ? (
                            <span className="metadoc-toc__entry-select-readonly">
                              {displayLabel}
                            </span>
                          ) : (
                            <div onClick={(e) => e.stopPropagation()}>
                              <Select
                                name={`document-type-${entry.id}`}
                                options={options
                                  .filter(
                                    (opt) =>
                                      opt.document_type !== entry.category,
                                  )
                                  .map((opt) => ({
                                    label: getDocumentTypeLabel(
                                      opt.document_type,
                                    ),
                                    value: opt.document_type,
                                  }))}
                                value={{
                                  label: displayLabel,
                                  value: entry.category,
                                }}
                                onChange={(selectedOption) =>
                                  handleDocumentTypeChange(
                                    entry,
                                    selectedOption,
                                  )
                                }
                                readOnly={
                                  disabled || updatingEntryId === entry.id
                                }
                                isClearable={false}
                                menuPosition="fixed"
                                menuShouldScrollIntoView={false}
                                classNames="metadoc-toc__entry-select"
                              />
                            </div>
                          )}
                        </>
                      )}
                      {!isDocumentGroup && displayLabel}
                    </div>
                    {isDocumentGroup &&
                      !entry.isPendingDelete &&
                      !isEntryReadOnly(entry) &&
                      onAssignDocumentGroupTag &&
                      onRemoveDocumentGroupTag &&
                      enabledTagTypes.length > 0 && (
                        <DocumentGroupTagMenu
                          assignedTags={entry.documentGroupTags ?? []}
                          enabledTypes={enabledTagTypes}
                          onAssign={(type) => void handleAssignTag(entry, type)}
                          onRemove={(tag) => void handleRemoveTag(entry, tag)}
                          disabled={disabled || tagActionEntryId === entry.id}
                        />
                      )}
                    {isDocumentGroup &&
                      !entry.isPendingDelete &&
                      isEntryReadOnly(entry) &&
                      (entry.documentGroupTags?.length ?? 0) > 0 && (
                        <DocumentGroupTagMenu
                          assignedTags={entry.documentGroupTags ?? []}
                          enabledTypes={enabledTagTypes}
                          readOnly
                        />
                      )}
                    {entry.isPendingDelete &&
                    onCancelPendingDelete &&
                    entry.documentGroupId ? (
                      <button
                        className="metadoc-toc__entry-delete metadoc-toc__entry-delete--undo"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCancelPendingDelete(entry.documentGroupId!);
                        }}
                        aria-label="Undo pending delete"
                        title="Undo pending delete"
                        disabled={disabled}
                      >
                        <span className="material-icons">undo</span>
                      </button>
                    ) : (
                      onDeleteEntry &&
                      !isEntryReadOnly(entry) && (
                        <button
                          className="metadoc-toc__entry-delete"
                          onClick={(e) => handleDeleteClick(e, entry)}
                          aria-label="Delete entry"
                          title="Delete entry"
                          disabled={disabled || isEntryReadOnly(entry)}
                        >
                          <span className="material-icons">delete</span>
                        </button>
                      )
                    )}
                    {isEntryReadOnly(entry) && isDocumentGroup && (
                      <span
                        className="metadoc-toc__entry-lock"
                        title="Delete disabled after AI run in Ops view"
                        aria-hidden="true"
                      >
                        <span className="material-icons">lock</span>
                      </span>
                    )}
                  </div>
                  {(isAiEditableDocumentGroup ||
                    entry.provider ||
                    facility ||
                    editableMetadataByEntryId[entry.id]) && (
                    <div className="metadoc-toc__entry-metadata">
                      {(isAiEditableDocumentGroup || entry.provider) && (
                        <div className="metadoc-toc__entry-metadata-row">
                          {isAiEditableDocumentGroup ? (
                            <div onClick={(e) => e.stopPropagation()}>
                              <TextInput
                                name={`document-provider-${entry.id}`}
                                value={providerValue}
                                classNames="metadoc-toc__entry-metadata-input metadoc-toc__entry-provider-input"
                                onChange={(event) => {
                                  handleEditableMetadataChange(
                                    entry,
                                    "provider",
                                    event.target.value,
                                  );
                                }}
                                onBlur={(event) => {
                                  void handleDocumentMetadataSave(
                                    entry,
                                    "provider",
                                    event.target.value,
                                  );
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    event.currentTarget.blur();
                                  }
                                }}
                                readOnly={
                                  disabled || updatingEntryId === entry.id
                                }
                                placeholder="Add provider"
                                maxLength={255}
                              />
                            </div>
                          ) : (
                            <span className="metadoc-toc__entry-provider">
                              {entry.provider}
                            </span>
                          )}
                        </div>
                      )}
                      {(isAiEditableDocumentGroup || facility) && (
                        <div className="metadoc-toc__entry-metadata-row">
                          {isAiEditableDocumentGroup ? (
                            <div onClick={(e) => e.stopPropagation()}>
                              <TextInput
                                name={`document-facility-${entry.id}`}
                                value={facilityValue}
                                classNames="metadoc-toc__entry-metadata-input metadoc-toc__entry-facility-input"
                                onChange={(event) => {
                                  handleEditableMetadataChange(
                                    entry,
                                    "facility",
                                    event.target.value,
                                  );
                                }}
                                onBlur={(event) => {
                                  void handleDocumentMetadataSave(
                                    entry,
                                    "facility",
                                    event.target.value,
                                  );
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    event.currentTarget.blur();
                                  }
                                }}
                                readOnly={
                                  disabled || updatingEntryId === entry.id
                                }
                                placeholder="Add facility"
                              />
                            </div>
                          ) : (
                            <span className="metadoc-toc__entry-facility">
                              {facility}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {isDocumentGroup && groupingSource === "AI" ? (
                    <div className="metadoc-toc__entry-date">
                      <div onClick={(e) => e.stopPropagation()}>
                        <DatePicker
                          key={`date-${entry.id}-${entry.dateOfService ?? "empty"}`}
                          name={`document-date-${entry.id}`}
                          initialDate={STRING_TO_DATE(
                            entry.dateOfService ?? null,
                          )}
                          onDateChange={(selectedDate) =>
                            void handleDocumentDateChange(entry, selectedDate)
                          }
                          readOnly={disabled || updatingEntryId === entry.id}
                          className="metadoc-toc__entry-date-picker"
                          isClearable={false}
                        />
                      </div>
                    </div>
                  ) : (
                    entry.dateOfService && (
                      <div className="metadoc-toc__entry-date">
                        {entry.dateOfService}
                      </div>
                    )
                  )}
                </div>
              );
            })
          )}
        </div>

        {onAssignDocumentType &&
          groupingSource === "OPS" &&
          (!isOpsPostAiReadOnly || isSelectiveOpsReadonlyMode) && (
            <DocumentTypePalette
              onSelectType={onAssignDocumentType}
              onSelectTypeToRange={onAssignDocumentTypeToRange}
              disabled={disabled || isAiAnalysisReadOnly}
              selectedPages={selectedPages}
              currentPage={currentPage}
              totalPages={totalPages}
            />
          )}
      </div>
    </>
  );
};

export default MetaDocTableOfContents;
